/**
 * rng.ts — deterministic seeded PRNG (mulberry32).
 *
 * The RNG state lives inside GameState and is serialized, so the market,
 * random events, and any other stochastic system are fully reproducible.
 * Systems must use this, never Math.random(), to keep tests deterministic.
 */

export interface RngState {
  seed: number;
}

export function createRng(seed = 0x9e3779b9): RngState {
  return { seed: seed >>> 0 };
}

/** Advance the RNG and return a float in [0, 1). Mutates state. */
export function next(state: RngState): number {
  state.seed = (state.seed + 0x6d2b79f5) >>> 0;
  let t = state.seed;
  t = Math.imul(t ^ (t >>> 15), t | 1);
  t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
}

/** Float in [min, max). */
export function range(state: RngState, min: number, max: number): number {
  return min + next(state) * (max - min);
}

/** Integer in [min, max] inclusive. */
export function int(state: RngState, min: number, max: number): number {
  return Math.floor(range(state, min, max + 1));
}

/** True with probability p. */
export function chance(state: RngState, p: number): boolean {
  return next(state) < p;
}

/** Pick a random element from a non-empty array. */
export function pick<T>(state: RngState, arr: readonly T[]): T {
  return arr[int(state, 0, arr.length - 1)] as T;
}

/** Standard-normal sample via Box–Muller (used by the market walk). */
export function normal(state: RngState, mean = 0, stdDev = 1): number {
  let u = 0;
  let v = 0;
  while (u === 0) u = next(state);
  while (v === 0) v = next(state);
  const mag = Math.sqrt(-2.0 * Math.log(u));
  return mean + stdDev * mag * Math.cos(2.0 * Math.PI * v);
}
